/* global Office */

/** Reads the full workbook as bytes via the Office document API. */
export function getWorkbookBytes() {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 4194304 },
      (fileResult) => {
        if (fileResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(fileResult.error?.message || "getFileAsync failed"));
          return;
        }
        const file = fileResult.value;
        const slices = new Array(file.sliceCount);
        let remaining = file.sliceCount;
        for (let i = 0; i < file.sliceCount; i++) {
          file.getSliceAsync(i, (sliceResult) => {
            if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
              file.closeAsync();
              reject(new Error(sliceResult.error?.message || "getSliceAsync failed"));
              return;
            }
            slices[sliceResult.value.index] = sliceResult.value.data;
            remaining -= 1;
            if (remaining === 0) {
              file.closeAsync();
              const totalLen = slices.reduce((n, s) => n + s.length, 0);
              const combined = new Uint8Array(totalLen);
              let offset = 0;
              for (const s of slices) { combined.set(s, offset); offset += s.length; }
              resolve(combined);
            }
          });
        }
      }
    );
  });
}


/**
 * Open selected sheets in a new Excel window.
 * Uses ExcelJS to filter sheets and style the first (header) row blue,
 * then calls Excel.createWorkbook(base64) to open in a fresh Excel window.
 */
export async function openSheetsInNewWindow(sheetNames = [], title = "") {
  const selected = Array.from(
    new Set((sheetNames || []).map((s) => String(s).trim()).filter(Boolean))
  );
  if (!selected.length) throw new Error("აირჩიე მინიმუმ ერთი ფურცელი.");

  const bytes = await getWorkbookBytes();

  const ExcelJS = await import("exceljs/dist/exceljs.min.js");
  const Workbook = ExcelJS.default?.Workbook ?? ExcelJS.Workbook;
  const wb = new Workbook();
  await wb.xlsx.load(bytes.buffer);

  // Remove sheets that weren't selected
  const toRemove = [];
  wb.eachSheet((ws) => {
    if (!selected.includes(ws.name)) toRemove.push(ws.id);
  });
  toRemove.forEach((id) => wb.removeWorksheet(id));

  if (wb.worksheets.length === 0) {
    throw new Error("არჩეული სახელების შესაბამისი ფურცელი სამუშაო წიგნში ვერ მოიძებნა.");
  }

  // Set workbook title (visible in Excel → File → Info → Properties)
  if (title) wb.title = title;

  // Style the first row of each remaining sheet with blue background + white bold text
  wb.eachSheet((ws) => {
    if (ws.rowCount < 1) return;
    const headerRow = ws.getRow(1);
    headerRow.eachCell({ includeEmpty: false }, (cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF007072" }, // teal-blue (matches PDF export header color)
      };
      cell.font = {
        ...(cell.font || {}),
        color: { argb: "FFFFFFFF" },
        bold: true,
      };
    });
    headerRow.commit();
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title || selected[0] || "export"}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
